/**
 * Cola en memoria, acotada y sin dependencias del navegador, para los eventos
 * de diagnóstico que el cliente envía por lotes al servidor.
 *
 * Se mantiene pura a propósito: el envío (timers + HTTP) vive en el adaptador
 * que la usa, así la parte con reglas —recorte, tope de cola, tamaño de lote—
 * se puede probar sin DOM.
 */

export type DiagnosticsContext = Record<string, string | number | boolean | null>;

export type DiagnosticsEntry = {
  event: string;
  at: string;
  context?: string;
};

export type DiagnosticsQueueOptions = {
  maxQueue: number;
  maxBatch: number;
};

export type DiagnosticsQueue = {
  /** Devuelve si la entrada entró de verdad (false = cola llena, se descarta). */
  enqueue(event: string, context: DiagnosticsContext | undefined, at: string): boolean;
  /** Extrae hasta `maxBatch` entradas y las quita de la cola. */
  takeBatch(): DiagnosticsEntry[];
};

/**
 * Mismo tope que el validador del servidor. Recortar aquí evita que un contexto
 * gigante haga fallar la validación y se pierda el lote entero.
 */
const MAX_CONTEXT_CHARS = 2_000;

export function createDiagnosticsQueue(options: DiagnosticsQueueOptions): DiagnosticsQueue {
  const entries: DiagnosticsEntry[] = [];

  return {
    enqueue(event, context, at) {
      if (entries.length >= options.maxQueue) {
        return false;
      }

      entries.push({
        event,
        at,
        ...(context ? { context: JSON.stringify(context).slice(0, MAX_CONTEXT_CHARS) } : {}),
      });

      return true;
    },

    takeBatch() {
      return entries.splice(0, options.maxBatch);
    },
  };
}
